import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Send, Trash2, History } from "lucide-react";
import { ToothMascot } from "@/components/site/ToothMascot";

export const SUGGESTIONS = [
  "When should my child first see a dentist?",
  "How much toothpaste for a 2 year old?",
  "My child has a toothache at night, what do I do?",
  "My child's tooth got knocked out!",
  "Is thumb-sucking bad for teeth?",
  "How does the online consultation work?",
];

const Typing = () => (
  <span className="flex items-center gap-1 py-1" aria-label="Sparkle is typing">
    {[0, 150, 300].map((d) => <span key={d} className="h-2 w-2 animate-bounce rounded-full bg-teal-500" style={{ animationDelay: `${d}ms` }} />)}
  </span>
);

const Bubble = ({ m }) => (
  <div className={`flex items-end gap-2 ${m.role === "user" ? "justify-end" : "justify-start"}`}>
    {m.role !== "user" && <ToothMascot className="h-8 w-8 shrink-0 rounded-full bg-white p-0.5 shadow-sm" />}
    <div className={`max-w-[85%] whitespace-pre-wrap break-words rounded-2xl px-4 py-3 text-[15px] leading-relaxed ${m.role === "user" ? "rounded-br-md bg-teal-600 text-white" : "rounded-bl-md bg-white text-slate-700 shadow-sm"}`}>
      {m.text || <Typing />}
    </div>
  </div>
);

export const ChatPanel = ({ chat, compact = false }) => {
  const [input, setInput] = useState("");
  const end = useRef(null);
  useEffect(() => { end.current?.scrollIntoView({ behavior: "smooth", block: "nearest" }); }, [chat.msgs]);
  const go = (q) => { chat.send(q); setInput(""); };

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex items-center justify-between border-b border-teal-100 bg-white/70 px-4 py-2 text-xs font-semibold text-slate-500">
        {chat.isSignedIn
          ? <span className="flex items-center gap-1" data-testid="ai-chat-memory-on"><History className="h-3.5 w-3.5 text-teal-600" />Chats are saved to your account</span>
          : <span data-testid="ai-chat-memory-off"><Link to="/sign-in" className="text-teal-700 underline">Sign in</Link> so Sparkle remembers your chats</span>}
        <button onClick={chat.clear} className="flex items-center gap-1 hover:text-rose-600" data-testid="ai-chat-clear-button"><Trash2 className="h-3.5 w-3.5" />Clear</button>
      </div>
      <div className="flex-1 space-y-4 overflow-y-auto overscroll-contain p-4 sm:p-5" data-testid="ai-chat-messages">
        {chat.msgs.map((m, i) => <Bubble key={i} m={m} />)}
        {chat.msgs.length === 1 && (
          <div className={`grid gap-2 pl-10 ${compact ? "" : "md:grid-cols-2"}`}>
            {SUGGESTIONS.map((s) => (
              <button key={s} onClick={() => go(s)} data-testid="ai-chat-suggestion"
                className="rounded-xl border border-teal-200 bg-white px-3 py-2.5 text-left text-sm font-semibold text-teal-700 transition-colors hover:bg-teal-50">{s}</button>
            ))}
          </div>
        )}
        <div ref={end} />
      </div>
      {chat.msgs.length > 1 && (
        <div className="flex gap-2 overflow-x-auto border-t border-teal-100 bg-white/80 px-3 py-2" data-testid="ai-chat-quick-topics">
          {SUGGESTIONS.slice(1, 5).map((s) => (
            <button key={s} onClick={() => go(s)} disabled={chat.busy}
              className="shrink-0 rounded-full border border-teal-200 bg-white px-3 py-1.5 text-xs font-semibold text-teal-700 hover:bg-teal-50 disabled:opacity-50">{s}</button>
          ))}
        </div>
      )}
      <form onSubmit={(e) => { e.preventDefault(); go(input); }} className="flex gap-2 border-t border-teal-100 bg-white p-3">
        <input value={input} onChange={(e) => setInput(e.target.value)} placeholder="Ask about teething, brushing, cavities..." data-testid="ai-chat-input"
          className="min-w-0 flex-1 rounded-full border border-slate-200 px-4 py-3 text-base outline-none focus:border-teal-500 sm:text-sm" />
        <button type="submit" disabled={chat.busy || !input.trim()} data-testid="ai-chat-send-button" aria-label="Send"
          className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-[#ff6b6b] text-white transition-transform active:scale-95 disabled:opacity-50"><Send className="h-5 w-5" /></button>
      </form>
      <p className="bg-white px-4 text-[11px] text-slate-400" style={{ paddingBottom: "max(0.5rem, env(safe-area-inset-bottom))" }}>General guidance only, not a diagnosis. For emergencies call +91 98206 49411.</p>
    </div>
  );
};
